/** Drift for the tour draft (plan §3 `useTourApply`, D3): the server has no tour
 *  changeset, so a 409 is answered by refetching the detail and comparing.
 *
 *  - `tourFreshValues` is the DraftProvider's `freshFrom` (`membershipFreshFrom` over the
 *    tour's `MEMBERSHIP_PATHS`): every baseline value as it stands, membership lists with
 *    an automatic merge (`mergedTo`, `mergeIdList`), and a tombstone (current null) for
 *    every entry of a saved row the server no longer has.
 *  - `rebaseChanges` is the same comparison over the changes of one save: the drifted
 *    ones (their `from` no longer deep-equals the server's value — Keep mine / Take
 *    theirs), and the changes to resend when nothing drifted, membership lists merged
 *    onto the server's. A change the server already made (its value equals `to`) is
 *    neither: it is dropped, as REBASE drops it from the draft.
 *
 *  Rows the draft creates (negative ids) never drift: the server has never seen them. */
import { membershipFreshFrom } from '@/features/records/collections/drift';
import { isMembershipPath, mergeIdList } from '@/features/records/collections/membership';
import {
  changeKey,
  draftValuesEqual,
  type DraftWireChange,
  type RecordDriftedField,
  type RecordKey,
} from '@/features/records/draft/changeSet';

import { MEMBERSHIP_PATHS } from './collections';

/** DraftProvider `freshFrom` for the tour record. Stable (module constant). */
export const tourFreshValues = membershipFreshFrom(MEMBERSHIP_PATHS);

export interface RebasedChanges {
  /** What to resend (membership lists merged onto the server's). */
  changes: DraftWireChange[];
  /** Changes the server moved under — the save must not go ahead. */
  drifted: RecordDriftedField[];
}

/** One save's changes against fresh baselines (after a 409). */
export function rebaseChanges(
  changes: readonly DraftWireChange[],
  baselines: Record<RecordKey, Record<string, unknown>>,
): RebasedChanges {
  const out: DraftWireChange[] = [];
  const drifted: RecordDriftedField[] = [];
  for (const c of changes) {
    const path = c.field ?? '';
    const rec = `${c.entity}:${c.id}`;
    if (!path || c.id < 0) {
      out.push(c);
      continue;
    }
    const row = baselines[rec];
    const current = row ? row[path] : null;
    if (!row) {
      drifted.push({
        key: changeKey(rec, path),
        entity: c.entity,
        id: c.id,
        field: path,
        current: null,
        changed_by: null,
        gone: true,
      });
      continue;
    }
    if (draftValuesEqual(c.from, current)) {
      out.push(c);
    } else if (draftValuesEqual(c.to, current)) {
      // The server already holds the wanted value: nothing left to send for it.
    } else if (isMembershipPath(path, MEMBERSHIP_PATHS)) {
      out.push({ ...c, from: current, to: mergeIdList(c.from, c.to, current) });
    } else {
      drifted.push({ key: changeKey(rec, path), entity: c.entity, id: c.id, field: path, current, changed_by: null });
    }
  }
  return { changes: out, drifted };
}
